import { ChatText, Check, Clock, Horse, LightningA, Repeat } from '@phosphor-icons/react';
import classNames from 'classnames';
import { ActivityTypeDefaultEnum, AvailabilityStatusEnum } from 'openapi';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarInitials, AvatarSize } from 'ui/Avatar';
import { contactInitials } from 'utilities/Contact';
import { bloodlineString, informalHorseName } from 'utilities/Horse';
import { activityNameInCal, CalendarActivity, TimeScale } from 'utilities/Planning';

interface Props {
  activity: CalendarActivity;
  timeScale: TimeScale; // Which timescale is used in the calendar
  showHorseName: boolean; // Force to show horse names
  spacious: boolean; // Do we have enough space to do a less compact layout
  fixedActivityType?: ActivityTypeDefaultEnum; // No need to show the activity type
  showContactAvatar: boolean;
  showBreedingBoxNumber: boolean;
}

/**
 * A component that is used within an activity that is shown in the calendar.
 */
export default function ActivityItemContent({
  activity,
  timeScale,
  showHorseName,
  spacious,
  fixedActivityType,
  showContactAvatar,
  showBreedingBoxNumber,
}: Props): JSX.Element {
  const { t } = useTranslation();

  // We show the horse in the title when we have a spacious setup and a note.
  // This way we can show the note within the ActivityItem.
  const showHorseInTitle = useMemo(() => {
    return spacious && activity.extraInfo && showHorseName;
  }, [spacious, activity, showHorseName]);

  const titleLine = useMemo(() => {
    return (
      <p className='text-sm truncate flex items-center gap-0.5'>
        <span className='font-medium truncate'>{activityNameInCal(activity)}</span>
        {activity.isAutomaticallyPlanned && <LightningA className='shrink-0' />}
        {activity.availabilityStatus === AvailabilityStatusEnum.TENTATIVE && <Clock className='shrink-0' />}
        {activity.rRule && <Repeat className='shrink-0' />}
        {activity.extraInfo && <ChatText className='shrink-0' />}
        {showHorseInTitle && (
          <span className='grow text-xs ml-0.5'>
            <Horse className='shrink-0 inline-block' />
            {informalHorseName(activity.horse)}
          </span>
        )}
      </p>
    );
  }, [activity, showHorseInTitle]);

  const primaryAssignee = useMemo(() => {
    return activity.assignedTo.find(assignee => assignee.primary)?.contact;
  }, [activity]);

  const secondLine = useMemo(() => {
    if (showHorseName && !showHorseInTitle) {
      return (
        <p className='text-xs flex items-center justify-start'>
          <Horse className='shrink-0' />
          {informalHorseName(activity.horse)}
          <span className='ml-1 opacity-80 text-xs'>
            {spacious && fixedActivityType && activity.horse && bloodlineString(activity.horse) && `(${bloodlineString(activity.horse)})`}
          </span>
        </p>
      );
    }

    if (activity.extraInfo && timeScale !== TimeScale.TimeScale && (!spacious || showHorseInTitle)) {
      return <p className='text-xs opacity-90 truncate'>{activity.extraInfo}</p>;
    }

    return <p className='text-xs opacity-90 truncate'>{activity.startEndText}</p>;
  }, [activity, fixedActivityType, showHorseName, spacious, timeScale, showHorseInTitle]);

  return (
    <div className='w-full'>
      <div className='w-full pl-1 flex items-center'>
        <div className='-space-y-1 grow truncate'>
          {titleLine}
          {secondLine}
        </div>
        {activity.done && <Check weight='bold' className='ml-0.5 shrink-0' size={20} />}
        {showBreedingBoxNumber && activity.breedingBoxNumber !== undefined && (
          <div className=' h-1/2 flex items-center justify-center px-2 ml-1 rounded bg-neutral-900 text-white text-sm whitespace-nowrap'>
            {t('breeding-box-short', 'Box')} {activity.breedingBoxNumber}
          </div>
        )}
        {primaryAssignee && showContactAvatar && (
          <div className={classNames('pl-1', { 'mt-0.5': spacious })}>
            <AvatarInitials
              size={AvatarSize.XSmall}
              initials={contactInitials(primaryAssignee)}
              color={primaryAssignee.color}
              uuid={primaryAssignee.uid}
            />
          </div>
        )}
      </div>
      {timeScale === TimeScale.TimeScale && spacious && activity.is_private_reservation && (
        <div className='ml-1 border rounded text-xs px-0.5 border-black inline-block'>{t('private', 'Private')}</div>
      )}
      {timeScale === TimeScale.TimeScale && spacious && activity.extraInfo && <p className='text-xs mt-1 pl-1'>{activity.extraInfo}</p>}
    </div>
  );
}
